import axios, { AxiosRequestConfig, AxiosPromise } from 'axios';
import {
  accessToken,
  accessTokenForceQueryParam,
  accessTokenHeader,
  cdnTimestamp,
  getDomainAndPort,
  singleItemAccessToken,
} from './Utils';
import MyError from './Error';
import { clearAccessTokenAndIsLoggedIn } from 'store/thisUser/thisUserSlice';
import { store } from 'index';
import { IAppInstance, RootState } from 'store/store';
import {
  IProvider,
  IProviderItemView,
  ITagView,
  ItemSearchField,
  SurveyOwnerProperties,
} from 'store/providers/providerTypes';
import { NumberOrNull, ISearchParams, StringOrNull } from 'store/entity/types';
import surveyApi from './SurveyApi';
import { axiosSource } from './axiosConfig';
import {
  ClassTimeConflictResult,
  CompanyMemberLog,
  CompanyPDP,
  ICompany,
  ICompanyMember,
  ICompanyPermissionScheme,
  ICompanyRole,
  ICompanyRoleServer,
  ICompanyServer,
  ICompanyTemplate,
  IInvitedMember,
  IProject,
  IProjectMemberView,
  IProjectTemplate,
  IStreamingConfig,
  IUserRoleView,
} from 'store/companies/companiesTypes';
import { IServerUser, IUser } from 'store/users/usersType';
import {
  IAssignmentHistory,
  IAssignmentView,
  IMention,
  ITask,
  ITaskUpdateView,
} from 'store/tasks/tasksType';
import {
  IProviderTemplate,
  ITemplateAdmin,
  ITemplateReportMark,
} from 'store/templates/templatesSlice';
import {
  ICheckPoint,
  IQuestionsView,
  IReportView,
  ISurveyViewServer,
} from 'store/survey/surveyTypes';
import { ISingleItemAccessToken } from 'store/tokens/tokensSlice';
import { validate } from 'store/providers/providerUtils';
import {
  Participant,
  PDPClassServer,
  PDPProgram,
  Term,
  Topic,
} from 'store/pdp/types';
import cloneDeep from 'lodash/cloneDeep';
import UserMixpanel from '../utils/mixpanel/MixpanelClass';

const domainAndPort = getDomainAndPort();

const { protocolDomain, port } = domainAndPort;

export const baseURL = `${protocolDomain}${
  port !== '80' ? ':' + port : ''
}/man/rest`;
const ManApi = axios.create({
  baseURL,
});
export const jitsiUrl = domainAndPort.jitsiUrl || 'https://meet.rastech.ir';

ManApi.interceptors.response.use(undefined, (err) => {
  let error;
  if (err.response) {
    if (
      err.response.data.message === '403.unauthenticated' //||
      // err.response.data.message === '403.unauthorized'
    ) {
      store.dispatch(clearAccessTokenAndIsLoggedIn({ shouldLogOut: true }));
    }
    error = new MyError(err.response.data, err.response.status);
  } else if (err.request) {
    error = new MyError(err.request, 'request');
  } else {
    error = new MyError(err, 'request');
  }
  const newError = Object.assign(err, error);
  return Promise.reject(newError);
});

export default ManApi;
export type SimpleResponseStatus = 'Success' | 'Failed';
export interface SimpleResponse {
  status: SimpleResponseStatus;
  message: string;
}

export const getUserStatus = (state: RootState): AxiosPromise<IServerUser> => {
  return ManApi.get('/user/status', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const registerUser = (
  user: IUser,
  state: RootState
): AxiosPromise<IServerUser> => {
  return ManApi.post(
    '/user/register',
    {
      ...user,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: { ...accessToken(state) },
    }
  );
};

export function user_get(userId: number, state: RootState) {
  return ManApi.get('/user/get', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      userId,
      ...accessToken(state),
    },
  });
}

export function user_getAll(userIds: number[], state: RootState) {
  return ManApi.post('/user/getAll', userIds, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
}

export function user_changePhone(
  username: string,
  userId: string,
  currentPhone: string | null,
  newPhone: string | null,
  swapIfExists: boolean,
  state: RootState,
  newName?: string
) {
  return ManApi.post(
    '/user/changePhone',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        name: username,
        userId,
        currentNumber: currentPhone,
        newNumber: newPhone,
        newName,
        swapIfExist: swapIfExists,
        ...accessToken(state),
      },
    }
  );
}

export function userUpdate(state: RootState, user: IUser) {
  return ManApi.post('/user/update', user, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
}
export function updateAnalyseSurveys(providerId: number, state: RootState) {
  return ManApi.post(
    '/provider/updateAnalyseSurveys',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        providerId,
        ...accessToken(state),
      },
    }
  );
}

export function getOrCreateDirectConversation(
  state: RootState,
  userId: number
) {
  return ManApi.get('/user/getOrCreateDirectConversation', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      userId,
      ...accessToken(state),
    },
  });
}

export function provider_get(
  state: RootState,
  providerId: number | null,
  username: string | null
) {
  return ManApi.get('provider/get', {
    headers: {
      // ...accessTokenHeader(state),
    },
    params: {
      providerId,
      username,
      ...accessTokenForceQueryParam(state),
      ...singleItemAccessToken(state),
    },
  });
}

export const getProviderStatus = (
  state: RootState
): AxiosPromise<IProvider> => {
  return ManApi.get('provider/status', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};
export function provider_register(provider: IProvider, state: RootState) {
  return ManApi.post(
    'provider/register',
    {
      ...provider,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}
export function provider_update(provider: IProvider, state: RootState) {
  return ManApi.post(
    'provider/update',
    {
      ...provider,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}
export function surveyOwnerProperties_update(
  properties: SurveyOwnerProperties,
  token: string,
  state: RootState
) {
  return ManApi.post(
    'survey/updateOwnerProperties',
    {
      ...properties,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        owner_token: token,
        ...accessToken(state),
      },
    }
  );
}

export function provider_addDocument(
  providerDocument: any,
  file: any,
  filename: string,
  state: RootState,
  progressCallBack: any
) {
  validate(providerDocument, file);
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'application-v2/json',
      'Content-Type': 'multipart/form-data',
      ...accessTokenHeader(state),
      'X-No-Timeout': 'true',
    },

    onUploadProgress:
      typeof progressCallBack === 'function'
        ? (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progressCallBack(progress);
          }
        : undefined,
    params: {
      ...accessToken(state),
    },
    timeout: 0,
  };
  const data = new FormData();
  data.append('file', file, filename);
  data.append('providerDocument', JSON.stringify(providerDocument));

  const mx = UserMixpanel.getInstance();
  mx.sendUploadDocumentTrack(
    `${providerDocument.type} - ${providerDocument.name}`
  );
  return ManApi.post('provider/addDocument', data, config);
}
export function provider_addDocumentVersion(
  providerItemId: number,
  file: any,
  filename: string,
  versionName: string,
  versionMessage: string | null,
  state: RootState,
  progressCallBack: any
) {
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'application/json',
      ...accessTokenHeader(state),
      'Content-Type': 'multipart/form-data',
      'X-No-Timeout': 'true',
    },

    onUploadProgress:
      typeof progressCallBack === 'function'
        ? (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progressCallBack(progress);
          }
        : undefined,

    params: {
      providerDocumentId: providerItemId,
      ...accessToken(state),
    },
    timeout: 0,
  };
  const data = new FormData();
  data.append('file', file, filename);
  data.append(
    'versionProperties',
    JSON.stringify({ name: versionName, message: versionMessage })
  );
  data.append('documentProperties', JSON.stringify({ displayName: filename }));
  return ManApi.post('provider/addVersion', data, config);
}

export function provider_getDocumentsInFolder(
  providerDocumentId: number,
  state: RootState,
  start = 0,
  count = 200,
  includeDeleted = false
) {
  return ManApi.get('provider/getDocumentsInFolder', {
    headers: {
      // ...accessTokenHeader(state),
    },
    params: {
      count,
      providerDocumentId,
      start,
      includeDeleted,
      ...accessTokenForceQueryParam(state),
    },
  });
}

export function provider_addFolder(item: IProviderItemView, state: RootState) {
  return ManApi.post(
    'provider/addFolder',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}
export function provider_importContentFromRemote(
  sourceItemId: number,
  targetItemId: number,
  remoteUrl: string,
  readAccessToken: string,
  forceCopy: boolean,
  excludeSrcFolder: boolean,
  state: RootState
) {
  return ManApi.post(
    'provider/importContentFromOtherProvider',
    {
      replaceTags: [],
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        sourceItemId,
        targetItemId,
        remoteUrl,
        readAccessToken,
        forceCopy,
        excludeSrcFolder,
        ...accessToken(state),
      },
    }
  );
}

export function provider_addQuill(item: IProviderItemView, state: RootState) {
  return ManApi.post(
    'provider/createQuill',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}

export function provider_addSurvey(item: IProviderItemView, state: RootState) {
  return ManApi.post(
    'provider/createSurvey',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}

export function provider_addLink(item: IProviderItemView, state: RootState) {
  return ManApi.post(
    'provider/addLink',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}
export function provider_addExternalLink(
  item: IProviderItemView,
  state: RootState
) {
  return ManApi.post(
    'provider/addExternalLink',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}

export function provider_addSearchTag(
  item: IProviderItemView,
  state: RootState
) {
  return ManApi.post(
    'provider/addSearchTag',
    {
      ...item,
      id: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}

export const addProviderItemLink = (
  sourceItemId: number,
  destinationItemId: number,
  description: string,
  state: RootState
) =>
  ManApi.post(
    'provider/addProviderItemLink',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        sourceItemId,
        destinationItemId,
        description,
        ...accessToken(state),
      },
    }
  );

export const updateProviderItemLinkById = (
  item: IProviderItemView,
  state: RootState
) =>
  ManApi.post(
    'provider/updateProviderItemLink',
    {
      ...item,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        ...singleItemAccessToken(state),
      },
    }
  );

export const deleteProviderItemLinkById = (itemId: number, state: RootState) =>
  ManApi.post(
    'provider/deleteProviderItemLinkById',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        itemId,
        ...accessToken(state),
      },
    }
  );

export const restoreProviderItem = (itemId: number, state: RootState) =>
  ManApi.get('provider/restoreDocument', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerDocumentId: itemId,
      ...accessToken(state),
    },
  });

export const addReportItem = (state: RootState, item: IProviderItemView) => {
  return ManApi.post(
    'provider/createReport',
    {
      ...item,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const updateReportItem = (state: RootState, item: IProviderItemView) => {
  return ManApi.post(
    'provider/updateReport',
    {
      ...item,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        ...singleItemAccessToken(state),
      },
    }
  );
};

export function provider_updateQuill(
  providerQuillId: number | null,
  providerQuill: IProviderItemView,
  deltaJson: any,
  htmlString: any,
  fileNameToBlob: any,
  state: RootState,
  progressCallBack?: any
) {
  const config: AxiosRequestConfig = {
    headers: {
      ...accessTokenHeader(state),
      'Content-Type': 'multipart/form-data',
    },

    onUploadProgress:
      typeof progressCallBack === 'function'
        ? (progressEvent: any) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progressCallBack(progress);
          }
        : undefined,
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
      providerQuillId,
    },
  };

  const data = new FormData();
  // replace '==' with '==='
  if (htmlString !== null) {
    data.append('html', htmlString);
  }
  if (deltaJson !== null) {
    data.append('deltaJson', JSON.stringify(deltaJson));
  }
  if (providerQuill !== null) {
    data.append('providerQuill', JSON.stringify(providerQuill));
  }
  for (const fileName of Object.keys(fileNameToBlob)) {
    data.append('attachments', fileNameToBlob[fileName], fileName);
  }
  return ManApi.post('provider/updateQuill', data, config);
}

export function provider_deleteDocument(
  providerDocumentId: number,
  state: RootState
) {
  return ManApi.get('provider/deleteDocument', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerDocumentId,
      ...accessToken(state),
    },
  });
}

export function provider_updateThumbnail(
  blob: any,
  providerItemId: number,
  state: RootState
) {
  const header = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const config = {
    ...header,
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
      providerItemId,
    },
  };
  const data = new FormData();
  // replace '==' with '==='
  if (blob !== null) {
    data.append('file', blob);
  } else {
    data.append('!!!!', blob);
  }
  return ManApi.post('provider/updateItemThumbnail', data, config);
}

export const updateFolder = (item: IProviderItemView, state: RootState) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
    },
  };
  return ManApi.post('provider/updateFolder', item, config);
};

export const updateSurvey = (item: IProviderItemView, state: RootState) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
    },
  };
  return ManApi.post('provider/updateSurvey', item, config);
};

export const updateDocument = (item: IProviderItemView, state: RootState) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
      providerDocumentId: item.id,
    },
  };
  const data = new FormData();
  data.append(
    'providerItem',
    new Blob([JSON.stringify(item)], {
      type: 'application/json',
    })
  );
  return ManApi.post('provider/updateDocumentProperties', data, config);
};
export const removeVersion = (
  item: IProviderItemView,
  versionId: number,
  state: RootState
) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
      providerDocumentId: item.id,
      versionId: versionId,
    },
  };
  return ManApi.post('provider/removeVersion', {}, config);
};

export const updateLink = (item: IProviderItemView, state: RootState) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  };
  return ManApi.post('provider/updateLink', item, config);
};

export const updateExternalLink = (
  item: IProviderItemView,
  state: RootState
) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  };
  return ManApi.post('provider/updateExternalLink', item, config);
};

export const updateSearchTag = (item: IProviderItemView, state: RootState) => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  };
  return ManApi.post('provider/updateSearchTag', item, config);
};

export const createReadLimitation = (
  state: RootState,
  providerItemId: number
): AxiosPromise<IProviderItemView> =>
  ManApi.get('provider/createLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const createEditLimitation = (
  state: RootState,
  providerItemId: number
): AxiosPromise<IProviderItemView> =>
  ManApi.get('provider/createEditLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const createReportLimitation = (
  state: RootState,
  providerItemId: number
): AxiosPromise<IProviderItemView> =>
  ManApi.get('provider/createReportLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const createChangeDataLimitation = (
  state: RootState,
  providerItemId: number
): AxiosPromise<IProviderItemView> =>
  ManApi.get('provider/createChangeDataLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const removeReadLimitation = (
  state: RootState,
  providerItemId: number
) =>
  ManApi.get('provider/removeLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const removeEditLimitation = (
  state: RootState,
  providerItemId: number
) =>
  ManApi.get('provider/removeEditLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const removeReportLimitation = (
  state: RootState,
  providerItemId: number
) =>
  ManApi.get('provider/removeReportLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const removeChangeDataLimitation = (
  state: RootState,
  providerItemId: number
) =>
  ManApi.get('provider/removeChangeDataLimitationForProviderItem', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerItemId,
      ...accessToken(state),
    },
  });

export const getLimitation = (limitationId: number, state: RootState) =>
  ManApi.get('permission/getLimitation', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      id: limitationId,
      ...accessToken(state),
    },
  });

export const migrateLimitations = (
  data: Array<{
    userId: number;
    userName: string;
    roleId: number;
    roleName: string;
  }>,
  companyId: number,
  state: RootState
) =>
  ManApi.post('permission/applyUserToRoleLimitationMigration', data, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      ...accessToken(state),
    },
  });

export const permissionAddUser = (
  limitationId: number,
  userId: number,
  state: RootState
) => {
  return ManApi.get('permission/addUser', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      limitationId,
      userId,
      ...accessToken(state),
    },
  });
};

export const permissionRemoveUser = (
  limitationId: number,
  userId: number,
  state: RootState
) => {
  return ManApi.get('permission/removeUser', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      limitationId,
      userId,
      ...accessToken(state),
    },
  });
};

export const permissionAddRole = (
  limitationId: number,
  roleId: number,
  state: RootState
) => {
  return ManApi.get('permission/addRole', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      limitationId,
      roleId,
      ...accessToken(state),
    },
  });
};

export const permissionRemoveRole = (
  limitationId: number,
  roleId: number,
  state: RootState
) => {
  return ManApi.get('permission/removeRole', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      limitationId,
      roleId,
      ...accessToken(state),
    },
  });
};

export function permission_updateLimitation(limitation: any, state: RootState) {
  return ManApi.post(
    'permission/update',
    {
      ...limitation,
      exceptedUsers: null,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}

export function provider_getMyProviderSurveys(
  state: RootState,
  providerId: number
) {
  return ManApi.get('provider/getMyProviderSurveys', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      providerId,
    },
  });
}

export function tag_getMyTags(providerId: number, state: RootState) {
  return ManApi.get('tag/getMyTags', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...cdnTimestamp(),
      providerId,
    },
  });
}

export function tag_getOrCreateTags(
  state: RootState,
  providerId: number,
  tags: string[],
  parentId: number | null
) {
  return ManApi.get('tag/getOrCreateTags', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      parentId,
      tags,
      providerId,
      ...accessToken(state),
    },
  });
}

export function tag_updateTag(state: RootState, tag: ITagView) {
  return ManApi.post(
    'tag/updateTag',
    {
      ...tag,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
}
export function tag_deleteTag(state: RootState, tag: ITagView) {
  return ManApi.post(
    'tag/deleteTag',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        tagId: tag.id,
        ...accessToken(state),
      },
    }
  );
}

export function tag_restoreTag(state: RootState, tag: ITagView) {
  return ManApi.post(
    'tag/restoreTag',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        tagId: tag.id,
        ...accessToken(state),
      },
    }
  );
}

export function user_search(search: string, state: RootState) {
  return ManApi.get('user/getByPhoneNumber', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      search,
      ...accessToken(state),
    },
  });
}

export function user_updateUserInfoBasedOnJWT(
  service: number,
  _accessToken: string,
  token: string,
  _state: RootState
) {
  return ManApi.get('user/updateUserBasedOnJWTInfo', {
    headers: {
      Authorization: _accessToken,
      // ...accessTokenHeader(state),
    },
    params: {
      token,
      serviceId: service,
      // ...accessToken(state),
    },
  });
}

export function provider_search(
  query: string,
  variant: string | null,
  start: number,
  count: number,
  latitude: number | null = null,
  longitude: number | null = null,
  filters: string[],
  state: RootState
) {
  return ManApi.get('provider/search', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      count,
      query,
      start,
      filters,
      latitude,
      longitude,
      ...accessToken(state),
      variant,
    },
  });
}

export const appInstanceGet = (state: RootState) => {
  return ManApi.get('appInstance/get', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const appInstanceUpdate = (
  state: RootState,
  appInstance: IAppInstance
) => {
  return ManApi.post(
    'appInstance/update',
    {
      ...appInstance,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export function provider_getDocument(
  providerDocumentId: number,
  state: RootState,
  token?: string
) {
  return ManApi.get('provider/getDocument', {
    headers: {
      // ...accessTokenHeader(state),
    },
    params: {
      ...accessTokenForceQueryParam(state),
      providerDocumentId,
      ...accessToken(state),
      ...singleItemAccessToken(state, token),
    },
  });
}

export const getProviderDocuments = (
  providerDocumentIds: number[],
  state: RootState
): AxiosPromise<IProviderItemView[]> => {
  if (providerDocumentIds.length < 100) {
    return ManApi.get('provider/getDocumentsByIds', {
      params: {
        ...accessTokenForceQueryParam(state),
        ...singleItemAccessToken(state),
        ids: providerDocumentIds,
      },
    });
  }

  return ManApi.post('provider/getDocuments', providerDocumentIds, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
    },
  });
};

export const getProviderDocumentsByExtraIds = (
  providerDocumentIds: string[],
  state: RootState
): AxiosPromise<IProviderItemView[]> => {
  return ManApi.post('provider/getDocumentsByExtraIds', providerDocumentIds, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      ...singleItemAccessToken(state),
      extraIdGroup: 'bot',
    },
  });
};

export const proxyGet = (url: string) => {
  return ManApi.get('proxy/get', {
    params: {
      url,
    },
  });
};

export function provider_getFolderViewTypes(state: RootState) {
  return ManApi.get('provider/getFolderViewTypes', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
}

export const getAllCompany = (
  state: RootState,
  companyStub: boolean,
  projectStub: boolean = true
): AxiosPromise<ICompanyServer[]> => {
  return ManApi.get('company/getMyCompanies', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      companyStub,
      projectStub,
    },
  });
};

export const createCompany = (
  company: ICompany,
  state: RootState
): AxiosPromise<ICompanyServer> => {
  return ManApi.post(
    'company/create',
    {
      ...company,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const getCompany = (
  state: RootState,
  companyId: number,
  templateStub: boolean | null = null,
  companyStub: boolean | null = null,
  projectStub: boolean | null = null,
  taskStub: boolean | null = null
): AxiosPromise<ICompanyServer> => {
  return ManApi.get('company/get', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      companyStub,
      projectStub,
      taskStub,
      templateStub,
      ...accessToken(state),
      ...singleItemAccessToken(state),
    },
  });
};

export const migrateToProjectRoles = (
  state: RootState,
  companyId: number,
  toMigrateRoleNames: string[]
): AxiosPromise<ICompanyServer> => {
  return ManApi.post(
    'company/migrateCompanyProjectRoles',
    { toMigrateRoleNames: toMigrateRoleNames || [] },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        companyId,
        ...accessToken(state),
      },
    }
  );
};
export const migrateRole = (
  state: RootState,
  fromRole: number,
  toRole: number,
  deleteFrom: boolean
): AxiosPromise<ICompanyServer> => {
  return ManApi.post(
    'role/migrateRole',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        fromRole,
        toRole,
        delete: deleteFrom,
        ...accessToken(state),
      },
    }
  );
};

export const updateCompany = (
  company: ICompany,
  state: RootState
): AxiosPromise<ICompanyServer> => {
  return ManApi.post(
    'company/update',
    {
      ...company,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const addCompanyMember = (
  companyMember: ICompanyMember,
  state: RootState
): AxiosPromise<ICompanyMember> => {
  return ManApi.post(
    'company/addMember',
    {
      ...companyMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const companyAddMembers = (
  invitedMembers: IInvitedMember[],
  forceRename: boolean,
  companyId: number,
  state: RootState
): AxiosPromise<Array<IUser>> => {
  return ManApi.post('company/addMembers', invitedMembers, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      forceRename,
      ...accessToken(state),
    },
  });
};

export const removeCompanyMember = (
  companyMember: ICompanyMember,
  state: RootState
): AxiosPromise<{ message: string; status: string }> => {
  return ManApi.post(
    'company/removeMember',
    {
      ...companyMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const addCompanyTemplate = (
  companyTask: ICompanyTemplate,
  state: RootState
): AxiosPromise<ICompanyTemplate> => {
  return ManApi.post(
    'company/addTemplate',
    {
      ...companyTask,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const updateCompanyTemplate = (
  companyTask: ICompanyTemplate,
  state: RootState
): AxiosPromise<ICompanyTemplate> => {
  delete companyTask.task?.viewToken;
  return ManApi.post(
    'company/updateTemplate',
    {
      ...companyTask,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const pushCompanyTemplate = (
  state: RootState,
  templateId: number
): AxiosPromise<number> => {
  return ManApi.get('company/pushTemplate', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      templateId,
      ...accessToken(state),
    },
  });
};

export const getAllProjects = (
  state: RootState,
  companyId: number,
  projectStub: boolean
): AxiosPromise<Array<IProject>> => {
  return ManApi.get('project/getMyProjects', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      projectStub,
      ...accessToken(state),
    },
  });
};

export const createProject = (
  project: IProject,
  state: RootState
): AxiosPromise<IProject> => {
  return ManApi.post(
    'project/create',
    {
      ...project,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};
export const createBatchProject = (
  project: IProject,
  names: string[],
  templates: Array<IProjectTemplate>,
  state: RootState
): AxiosPromise<IProject[]> => {
  const payload = {
    projectView: project,
    names,
    templates,
  };

  return ManApi.post('project/batchAddProjects', payload, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const updateProject = (
  project: IProject,
  state: RootState
): AxiosPromise<IProject> => {
  return ManApi.post(
    'project/update',
    {
      ...project,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export function project_delete(projectId: number, state: RootState) {
  return ManApi.get('project/delete', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      projectId,
      ...accessToken(state),
    },
  });
}

export const getProject = (
  projectId: number,
  state: RootState
): AxiosPromise<IProject> => {
  return ManApi.get('project/get', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      projectId,
      ...accessToken(state),
    },
  });
};

export const addProjectMember = (
  projectMember: IProjectMemberView,
  state: RootState
): AxiosPromise<IProjectMemberView> => {
  return ManApi.post(
    'project/addMember',
    {
      ...projectMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const removeProjectMember = (
  projectMember: IProjectMemberView,
  state: RootState
): AxiosPromise<{ message: 'member.removed'; status: 'Success' }> => {
  return ManApi.post(
    'project/removeMember',
    {
      ...projectMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const updateProjectMember = (
  projectMember: IProjectMemberView,
  state: RootState
): AxiosPromise<IProjectMemberView> => {
  return ManApi.post(
    'project/updateMember',
    {
      ...projectMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const addTemplateToProject = (
  projectTemplate: IProjectTemplate,
  state: RootState
): AxiosPromise<IProjectTemplate> => {
  projectTemplate = cloneDeep(projectTemplate);
  delete (projectTemplate as any).task;
  return ManApi.post(
    'project/addTemplate',
    {
      ...projectTemplate,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const removeTemplateFromProject = (
  projectTemplate: IProjectTemplate,
  state: RootState
): AxiosPromise<{ message: 'template.removed'; status: 'Success' }> => {
  return ManApi.post(
    'project/removeTemplate',
    {
      ...projectTemplate,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const searchTasks = (
  state: RootState,
  searchParams: ISearchParams
): AxiosPromise<Array<ITask>> => {
  const { fieldValues, fieldValuesMap, excludeFields, ...querySearchParams } =
    searchParams;
  const data = new FormData();
  data.append(
    'fieldMap',
    new Blob([JSON.stringify(fieldValues || {})], {
      type: 'application/json',
    })
  );
  data.append(
    'fieldValuesMap',
    new Blob([JSON.stringify(fieldValuesMap || {})], {
      type: 'application/json',
    })
  );
  data.append(
    'excludeFields',
    new Blob([JSON.stringify(excludeFields || {})], {
      type: 'application/json',
    })
  );

  return ManApi.post('task/search', data, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...singleItemAccessToken(state),
      ...querySearchParams,
      ...accessToken(state),
      includeAssignmentHistory: true,
      includeProgressHistory: searchParams?.includeProgressHistory ?? false,
    },
  });
};

export const createTask = (
  state: RootState,
  task: ITask,
  dontSendMessages: boolean = false
): AxiosPromise<ITask> => {
  const config: AxiosRequestConfig = {
    headers: {
      Accept: 'application-v2/json',
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      dontSendMessages,
    },
  };
  return ManApi.post('task/create', task, config);
};
export const moveToProject = (
  state: RootState,
  taskId: number,
  projectId: number
): AxiosPromise<ITask> => {
  const config: AxiosRequestConfig = {
    // headers: {
    //   Accept: 'application/json',
    // },
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      taskId,
      projectId,
      ...accessToken(state),
    },
  };
  return ManApi.post('task/move', { taskId }, config);
};

export const updateTask = (
  state: RootState,
  task: ITask,
  dontSendMessages: boolean = false
): AxiosPromise<ITask> => {
  const config = {
    headers: {
      Accept: 'application-v2/json',
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      dontSendMessages,
    },
  };
  return ManApi.post('task/update', task, config);
};

export const resolveMention = (
  state: RootState,
  mentionId: number | undefined,
  taskId: number | undefined
): AxiosPromise<ITask> => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      taskId,
      mentionId,
      ...accessToken(state),
    },
  };
  return ManApi.post('task/resolveMentions', {}, config);
};

export const undraftTask = (
  state: RootState,
  taskId: number,
  includeSubTasks: boolean
): AxiosPromise<ITaskUpdateView> => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      taskId,
      includeSubTasks,
      ...accessToken(state),
    },
  };
  return ManApi.post('task/undraft', {}, config);
};
export const draftTask = (
  state: RootState,
  taskId: number
): AxiosPromise<ITaskUpdateView> => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      taskId,
      ...accessToken(state),
    },
  };
  return ManApi.post('task/draft', {}, config);
};
export const cloneTask = (
  state: RootState,
  tasks: number[]
): AxiosPromise<ITask[]> => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  };
  return ManApi.post('task/clone', tasks, config);
};
export const sendMessageToAssignee = (
  state: RootState,
  taskId: number,
  message: string
): AxiosPromise<SimpleResponse> => {
  const config = {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      taskId,
      message,
    },
  };
  return ManApi.post('task/sendMessageToAssignee', {}, config);
};

export const getSingleTask = (
  state: RootState,
  taskId: number,
  includeAssignmentHistory?: boolean,
  includeProgressHistory?: boolean,
  includeParentTasks?: boolean
): AxiosPromise<ITask> => {
  return ManApi.get('task/get', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      taskId,
      includeAssignmentHistory,
      includeProgressHistory,
      includeParentTasks,
      ...accessToken(state),
      ...singleItemAccessToken(state),
    },
  });
};

export const restoreTask = (
  state: RootState,
  taskId: number
): AxiosPromise<ITask> => {
  return ManApi.post(
    'task/restore',
    { taskId },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        taskId,
      },
    }
  );
};

export const deleteSingleTask = (
  state: RootState,
  taskId: number
): AxiosPromise<ITaskUpdateView> => {
  return ManApi.post(
    'task/delete',
    { taskId },
    {
      headers: {
        Accept: 'application-v2/json',
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        taskId,
      },
    }
  );
};

export const taskMark = (
  state: RootState,
  assignment: IAssignmentView
): AxiosPromise<IAssignmentView> => {
  return ManApi.post(
    'task/mark',
    {
      ...assignment,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const submitDevice = (
  state: RootState,
  name: string,
  instanceId: string
) => {
  return ManApi.post(
    'user/submitDevice',
    {
      name,
      instanceId,
      publicKeyBase64: 'hassan goft chert bezar :)',
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export function provider_importContentFromOtherProvider(
  sourceItemId: number,
  targetItemId: number,
  forceCopy: boolean,
  state: RootState
) {
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/json',
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      sourceItemId,
      targetItemId,
      forceCopy,
    },
  };
  return ManApi.post('provider/importContentFromOtherProvider', {}, config);
}

export const taskGetReport = (
  state: RootState,
  templateReport: ITemplateReportMark,
  startDate: number,
  toDate: number,
  projectId: NumberOrNull,
  parentId: NumberOrNull,
  templateId: number,
  companyId: number,
  assigneeId: NumberOrNull,
  splitMonths: number,
  splitField: StringOrNull,
  fieldValues: { [key: string]: string }
) => {
  const data = new FormData();
  data.append(
    'templateReport',
    new Blob([JSON.stringify(templateReport)], {
      type: 'application/json',
    })
  );
  data.append(
    'fieldMap',
    new Blob([JSON.stringify(fieldValues)], {
      type: 'application/json',
    })
  );
  return ManApi.post('task/getReport', data, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      startDate,
      toDate,
      projectId,
      parentId,
      templateId,
      companyId,
      assigneeId,
      splitMonths,
      splitField,
    },
  });
};

export const addProviderTemplate = (
  state: RootState,
  providerTemplate: IProviderTemplate
) => {
  return ManApi.post('appInstance/addTemplate', providerTemplate, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const updateProviderTemplate = (
  state: RootState,
  providerTemplate: IProviderTemplate
) => {
  return ManApi.post('appInstance/updateTemplate', providerTemplate, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getAllProviderTemplates = (
  state: RootState,
  appInstanceId: number
) => {
  return ManApi.get('appInstance/getAllTemplates', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      appInstanceId,
      ...accessToken(state),
    },
  });
};

export const getProviderTemplate = (state: RootState, templateId: number) => {
  return ManApi.get('appInstance/getTemplate', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      templateId,
      ...accessToken(state),
    },
  });
};

export const addAdminToTemplate = (
  state: RootState,
  adminView: ITemplateAdmin
) => {
  return ManApi.post('appInstance/template/addAdmin', adminView, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const removeAdminFromTemplate = (
  state: RootState,
  adminView: ITemplateAdmin
) => {
  return ManApi.post('appInstance/template/removeAdmin', adminView, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getMyProviders = (state: RootState): AxiosPromise<IProvider[]> => {
  return ManApi.get('provider/providers', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export type UpdateRoleObject = {
  updateRoles: ICompanyRole[];
  deleted: number[];
};
export const addRole = (
  state: RootState,
  companyRole: ICompanyRoleServer
): AxiosPromise<UpdateRoleObject> => {
  return ManApi.post(
    'role/add',
    {
      ...companyRole,
    },
    {
      headers: {
        Accept: 'application-v2/json',
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const updateRole = (
  state: RootState,
  companyRole: ICompanyRoleServer
): AxiosPromise<UpdateRoleObject> => {
  return ManApi.post(
    'role/update',
    {
      ...companyRole,
    },
    {
      headers: {
        Accept: 'application-v2/json',
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const addAllPrefixRoles = (
  state: RootState,
  companyRole: ICompanyRoleServer
): AxiosPromise<IUserRoleView[]> => {
  return ManApi.post(
    'role/addAllPrefixRoles',
    {
      ...companyRole,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const deleteRole = (
  state: RootState,
  roleId: number
): AxiosPromise<UpdateRoleObject> => {
  return ManApi.get('role/delete', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      roleId,
      ...accessToken(state),
    },
  });
};

export const addUserRoles = (
  state: RootState,
  userRoleViews: IUserRoleView[]
): AxiosPromise<IUserRoleView[]> => {
  return ManApi.post('role/addUsers', userRoleViews, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const removeUserRoles = (
  state: RootState,
  userRoleViews: IUserRoleView[]
): AxiosPromise<{ successCount: number }> => {
  return ManApi.post('role/removeUsers', userRoleViews, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getAllProviders = (state: RootState, providersId: number[]) => {
  return ManApi.post('/provider/getAll', providersId, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getByManagerCompany = (
  state: RootState,
  companyId: number
): AxiosPromise<IProvider[]> => {
  return ManApi.get('/provider/getByManagerCompany', {
    cancelToken: axiosSource.token,
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      ...accessToken(state),
    },
  });
};

export const searchDocuments = (
  state: RootState,
  query: string,
  providerId?: number,
  start?: number,
  count?: number,
  searchFields?: ItemSearchField[],
  parentId?: number
): AxiosPromise<IProviderItemView[]> =>
  ManApi.get('/provider/searchDocuments', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      query,
      providerId,
      start,
      count,
      searchFields,
      parentId,
      ...accessToken(state),
    },
  });

export const updateCompanyMember = (
  projectMember: ICompanyMember,
  state: RootState
): AxiosPromise<ICompanyMember> => {
  return ManApi.post(
    'company/updateMember',
    {
      ...projectMember,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};
export const getCompanyMember = (
  userId: number,
  companyId: number,
  state: RootState
): AxiosPromise<ICompanyMember> => {
  return ManApi.get('company/getMember', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      userId,
      companyId,
      ...accessToken(state),
    },
  });
};

export const getCompanyMemberLogs = (
  userId: number,
  companyId: number,
  beforeId: number | null,
  count: number | null,
  state: RootState
): AxiosPromise<CompanyMemberLog[]> => {
  return ManApi.get('company/getCompanyMemberLogs', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      userId,
      companyId,
      beforeId,
      count,
      ...accessToken(state),
    },
  });
};

export const addMemberSignature = (
  state: RootState,
  companyId: number,
  userId: number,
  file: any
): AxiosPromise<ICompanyMember> => {
  const header = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const config = {
    ...header,
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      companyId,
      userId,
    },
  };
  const data = new FormData();
  data.append('file', file);

  return ManApi.post('/company/addMemberSignature', data, config);
};

export const addCompanyDocument = (
  state: RootState,
  companyId: number,
  type: string,
  file: any
): AxiosPromise<ICompanyServer> => {
  const header = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const config = {
    ...header,
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      type,
      companyId,
    },
  };
  const data = new FormData();
  data.append('file', file);

  return ManApi.post('/company/addFile', data, config);
};

export const removeMemberSignature = (
  state: RootState,
  companyId: number,
  userId: number
): AxiosPromise<ICompanyMember> =>
  ManApi.post(
    '/company/removeMemberSignature',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        companyId,
        userId,
        ...accessToken(state),
      },
    }
  );
export const removeCompanyDocument = (
  state: RootState,
  companyId: number,
  type: string
): AxiosPromise<ICompanyServer> =>
  ManApi.post(
    '/company/removeFile',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        companyId,
        type,
        ...accessToken(state),
      },
    }
  );

export const getAllCheckpoints = (
  reportsIds: number[],
  reportToken?: string,
  ownerToken?: string
): AxiosPromise<{ [reportId: number]: ICheckPoint[] }> => {
  if (reportsIds.length > 50)
    return ManApi.post('/survey/getAllCheckPoints', reportsIds, {
      params: {
        report_token: reportToken,
        owner_token: ownerToken,
      },
    });
  return ManApi.get('/survey/getAllCheckPoints', {
    params: {
      report_token: reportToken,
      owner_token: ownerToken,
      reportIds: reportsIds,
      ...cdnTimestamp(),
    },
  });
};

export const getAllSurveys = (
  surveyTokens: string[]
): AxiosPromise<{ [surveyToken: string]: ISurveyViewServer }> => {
  return surveyTokens.length > 20
    ? ManApi.post('/survey/getAllSurveys', surveyTokens)
    : ManApi.get('/survey/getAllSurveys', {
        params: {
          surveyTokens,
        },
        ...cdnTimestamp(),
      });
};

export const searchMainReport = (
  state: RootState,
  mandatoryTagIds: number[],
  providerId: number
): AxiosPromise<
  { itemId: number; reportToken: string; report: IReportView }[]
> => {
  return ManApi.get('/provider/searchSurveyReports', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      providerId,
      mandatoryTagIds: mandatoryTagIds.join(','),
      ...accessToken(state),
    },
  });
};

export const addSingleItemAccessToken = (
  state: RootState,
  singleItemAccessToken: ISingleItemAccessToken
) =>
  ManApi.post(
    '/permission/createSingleItemAccessToken',
    singleItemAccessToken,
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );

export const updateSingleItemAccessToken = (
  state: RootState,
  singleItemAccessToken: ISingleItemAccessToken
) =>
  ManApi.post(
    '/permission/updateSingleItemAccessTokens',
    singleItemAccessToken,
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );

export const deleteSingleItemAccessToken = (
  state: RootState,
  singleItemAccessTokenId: number
) =>
  ManApi.post(
    '/permission/deleteSingleItemAccessTokens',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        id: singleItemAccessTokenId,
      },
    }
  );

export const getSingleItemAccessTokens = (state: RootState, itemId?: number) =>
  surveyApi.get('/permission/getAllSingleItemAccessTokens', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      itemId,
    },
  });

export const getAllCategories = (state: RootState) => {
  return ManApi.get('/category/getAll', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const addPermissionScheme = (
  state: RootState,
  companyPermissionScheme: ICompanyPermissionScheme
): AxiosPromise<ICompanyPermissionScheme> => {
  return ManApi.post(
    'permissionScheme/add',
    {
      ...companyPermissionScheme,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const updatePermissionScheme = (
  state: RootState,
  companyPermissionScheme: ICompanyPermissionScheme
): AxiosPromise<ICompanyPermissionScheme> => {
  return ManApi.post(
    'permissionScheme/update',
    {
      ...companyPermissionScheme,
    },
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
      },
    }
  );
};

export const deletePermissionScheme = (
  state: RootState,
  permissionSchemeId: number
): AxiosPromise<boolean> => {
  return ManApi.post(
    'permissionScheme/delete',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        permissionSchemeId,
        ...accessToken(state),
      },
    }
  );
};

export const exportCompanyMembers = (state: RootState, companyId: number) => {
  // if (1 + 3 === 2 - (surveyToken === undefined ? 1 : 0))
  return surveyApi.post(
    '/company/exportMembers',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        companyId,
      },
      responseType: 'blob',
    }
  );
  // return axios({
  //   method: 'get',
  //   url:
  //     'https://cdn.rastech.ir/provider/Screen%20Shot%201397-08-01%20at%206.06.52%20PM.png',
  //   responseType: 'blob',
  // });
};

export const createPDPPanelRequest = (
  state: RootState,
  companyId: number,
  templateName: string
): AxiosPromise<CompanyPDP> => {
  return ManApi.post(
    'pdp/createPanel',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        companyId,
        templateName,
        ...accessToken(state),
      },
    }
  );
};

export const exportTopicStatsRequest = (
  state: RootState,
  termIds: number[]
) => {
  return surveyApi.post('/pdp/exportPDPReport', termIds, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
    responseType: 'blob',
  });
};

export const updatePDPConfigRequest = (
  state: RootState,
  pdpConfig: CompanyPDP
): AxiosPromise<CompanyPDP> => {
  return ManApi.post('pdp/updateConfig', pdpConfig, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const createOrUpdateTopicRequest = (
  state: RootState,
  topic: Topic
): AxiosPromise<Topic> => {
  let url = 'pdp/createTopic';
  if (topic.id) url = 'pdp/updateTopic';
  return ManApi.post(url, topic, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getAllTopicsRequest = (
  state: RootState,
  pdpId: number
): AxiosPromise<Topic[]> => {
  return ManApi.get('pdp/getAllTopics', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      pdpId,
      ...accessToken(state),
    },
  });
};

export const createOrUpdateTermRequest = (
  state: RootState,
  topic: Term
): AxiosPromise<Term> => {
  let url = 'pdp/createTerm';
  if (topic.id) url = 'pdp/updateTerm';
  return ManApi.post(url, topic, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getAllTermsRequest = (
  state: RootState,
  companyPDPId: number,
  includeDeleted: boolean
): AxiosPromise<Term[]> => {
  return ManApi.get('pdp/getAllTerms', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyPDPId,
      includeDeleted,
      ...accessToken(state),
    },
  });
};

export const getMyTermsRequest = (
  state: RootState,
  companyPDPId: number
): AxiosPromise<Term[]> => {
  return ManApi.get('pdp/getMyTerms', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyPDPId,
      ...accessToken(state),
    },
  });
};

export const getMyClassesRequest = (
  state: RootState,
  companyPDPId: number,
  startDate?: number,
  endDate?: number,
  termId?: number
): AxiosPromise<PDPClassServer[]> => {
  return ManApi.get('pdp/getMyClasses', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyPDPId,
      startDate,
      endDate,
      ...accessToken(state),
      termId,
    },
  });
};

export const getMyManagingTermsRequest = (
  state: RootState,
  companyPDPId: number
): AxiosPromise<Term[]> => {
  return ManApi.get('pdp/getMyManagingTerms', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyPDPId,
      ...accessToken(state),
    },
  });
};
export const getMyManagingClassesRequest = (
  state: RootState,
  companyPDPId: number,
  startDate?: number,
  endDate?: number,
  termId?: number
): AxiosPromise<PDPClassServer[]> => {
  return ManApi.get('pdp/getMyManagingClasses', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      termId,
      companyPDPId,
      startDate,
      endDate,
      ...accessToken(state),
    },
  });
};
export const queryClassesRequest = (
  state: RootState,
  companyPDPId: number,
  startDate?: number,
  endDate?: number,
  participantUserId?: number,
  topicId?: number,
  showDeleted?: boolean,
  isTemplate?: boolean,
  termId?: number
): AxiosPromise<PDPClassServer[]> => {
  return ManApi.get('pdp/queryClasses', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyPDPId,
      startDate,
      endDate,
      participantUserId,
      topicId,
      showDeleted,
      isTemplate,
      termId,
      ...accessToken(state),
    },
  });
};

export const getMyPDPInTermsRequest = (
  state: RootState,
  termId: number
): AxiosPromise<PDPProgram> => {
  return ManApi.get('pdp/getMyPDPInTerm', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      termId,
      ...accessToken(state),
    },
  });
};

export const createOrUpdatePDPProgramRequest = (
  state: RootState,
  program: PDPProgram
): AxiosPromise<PDPProgram> => {
  let url = 'pdp/createPersonalPDP';
  if (program.id) url = 'pdp/updatePersonalPDP';
  return ManApi.post(url, program, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const deletePDPProgramRequest = (
  state: RootState,
  programId: number
): AxiosPromise<PDPProgram> => {
  return ManApi.post(
    'pdp/deletePersonalPDP',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        ...accessToken(state),
        programId,
      },
    }
  );
};
export const createOrUpdateBatchPDPProgramRequest = (
  state: RootState,
  program: PDPProgram[]
): AxiosPromise<PDPProgram[]> => {
  return ManApi.post('pdp/batchCreateOrUpdatePersonalPDP', program, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getMyManagingPDPInTermsRequest = (
  state: RootState,
  termId: number
): AxiosPromise<PDPProgram[]> => {
  return ManApi.get('pdp/getMyManagingPDPInTerm', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      termId,
      ...accessToken(state),
    },
  });
};

export const createOrUpdateClassRequest = (
  state: RootState,
  clazz: PDPClassServer,
  skipConflictCheck?: boolean
): AxiosPromise<PDPClassServer> => {
  let url = 'pdp/createClass';
  if (clazz.id) url = 'pdp/updateClass';
  return ManApi.post(url, clazz, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
      skipConflictCheck,
    },
  });
};
export const getClassRequest = (
  state: RootState,
  classId: number
): AxiosPromise<PDPClassServer> => {
  return ManApi.get('pdp/getClass', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      classId,
      ...accessToken(state),
    },
  });
};

export const addParticipantsRequest = (
  state: RootState,
  classId: number,
  userIds: number[],
  group?: string,
  isCorrective?: boolean,
  correctiveForClass?: number
): AxiosPromise<PDPClassServer> => {
  return ManApi.post('pdp/addParticipants', userIds, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      classId,
      group,
      isCorrective,
      correctiveForClass,
      ...accessToken(state),
    },
  });
};
export const updateParticipantsRequest = (
  state: RootState,
  participants: Participant[]
): AxiosPromise<Participant[]> => {
  return ManApi.post('pdp/updateParticipants', participants, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};
export const sendMessagesToUserRequest = (
  state: RootState,
  classId: number,
  message: string,
  groups: string[]
): AxiosPromise<Participant[]> => {
  return ManApi.post('pdp/sendMessageToUsers', groups, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      classId,
      message,
      ...accessToken(state),
    },
  });
};

export const autoGradeParticipantsRequest = (
  state: RootState,
  classId: number,
  group: string | null,
  participant?: number
): AxiosPromise<PDPClassServer> => {
  return ManApi.post(
    'pdp/autoGradeClass',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        classId,
        groupKey: group,
        participantId: participant,
        ...accessToken(state),
      },
    }
  );
};

export const setMyGroupRequest = (
  state: RootState,
  classId: number,
  group: string
): AxiosPromise<PDPClassServer> => {
  return ManApi.post(
    'pdp/setMyGroup',
    {},
    {
      headers: {
        ...accessTokenHeader(state),
      },
      params: {
        classId,
        group,
        ...accessToken(state),
      },
    }
  );
};

export const checkClassTimeConflictsRequest = (
  state: RootState,
  classView: PDPClassServer
): AxiosPromise<ClassTimeConflictResult> => {
  return ManApi.post('pdp/checkClassTimeConflicts', classView, {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      ...accessToken(state),
    },
  });
};

export const getAllMentions = (
  state: RootState,
  companyId: string | number
): AxiosPromise<IMention[]> => {
  return ManApi.get('task/getAllMentions', {
    headers: {
      ...accessTokenHeader(state),
    },
    params: {
      companyId,
      ...accessToken(state),
    },
  });
};

export const getAllTasks = (
  state: RootState,
  taskIdList: Array<number | string>,
  includeProgressHistory: boolean = false,
  includeAssignmentHistory: boolean = false,
  includeParentTasks: boolean = false
): AxiosPromise<ITask[]> => {
  return ManApi.post('task/getAll', [...taskIdList], {
    headers: { ...accessTokenHeader(state) },
    params: {
      includeProgressHistory,
      includeAssignmentHistory,
      includeParentTasks,
    },
  });
};

export const getAllRecentlyFilledSurveys = (
  state: RootState,
  companyId: number | string,
  count: number
) => {
  return ManApi.get('provider/getMyRecentFilledSurveys', {
    headers: { ...accessTokenHeader(state) },
    params: { companyId, count },
  });
};

export const generateQuestionViaAI = (
  state: RootState,
  description: string,
  questionList: IQuestionsView[] | undefined,
  file: File | null,
  controller?: any
): AxiosPromise<any> => {
  let signal = null;
  if (controller) {
    signal = controller.signal;
  }
  const form = new FormData();
  form.append('form', file ?? '{}');
  if (questionList)
    form.append('currentQuestionList', JSON.stringify(questionList));
  return ManApi.post('survey/createChecklistWithAi', form, {
    headers: {
      ...accessTokenHeader(state),
      'Content-Type': file?.type ?? 'application/json',
    },
    params: { userMessage: description },
    signal: signal ?? undefined,
  });
};

export const getFlags = (
  state: RootState,
  flagName?: string,
  flagGroups?: string
) => {
  return ManApi.get('user/getMyFlags', {
    headers: { ...accessTokenHeader(state) },
    params: { flagName, flagGroups },
  });
};

export const addOrUpdateUserFlag = (
  state: RootState,
  flagName: string,
  flagValue: string,
  flagGroup: string
) => {
  return ManApi.post(
    'user/flag',
    {},
    {
      headers: { ...accessTokenHeader(state) },
      params: { flagName, flagValue, flagGroup },
    }
  );
};

export const ConvertMeetingVoiceToText = (
  state: RootState,
  formData: FormData,
  setProgress?: (progress: number) => void,
  abortController?: AbortController
): AxiosPromise<any> => {
  return ManApi.post('ai/createMeetingNote', formData, {
    headers: { ...accessTokenHeader(state), 'X-No-Timeout': 'true' },
    params: { ...accessToken(state) },
    signal: abortController?.signal,
    onUploadProgress: (progressEvent: any) => {
      if (progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setProgress?.(percent);
      }
    },
  });
};

export const syncDownStream = (
  state: RootState,
  companyId: number,
  downStreamConfigId: number
) => {
  return ManApi.post(
    'company/syncDownStreamItems',
    {},
    {
      headers: { ...accessTokenHeader(state) },
      params: { companyId, downStreamConfigId },
    }
  );
};

export const handleUpdateCompanyStreamingConfig = (
  state: RootState,
  companyId: number,
  streamingConfig: IStreamingConfig
) => {
  return ManApi.post(
    'company/updateStreamingConfig',
    { ...streamingConfig },
    {
      headers: { ...accessTokenHeader(state) },
      params: { companyId },
    }
  );
};

export const handleAddAttachementsToTask = (
  state: RootState,
  taskId: number,
  file: any,
  onProgressCallback: (progress: any) => void
) => {
  const form = new FormData();
  form.append('file', file);

  return ManApi.post('task/addAttachment', form, {
    headers: { ...accessTokenHeader(state) },
    params: { taskId },
    onUploadProgress: (progressEvent: any): void => {
      onProgressCallback?.(progressEvent);
    },
  });
};

export const markTaskAssignmentHistoryHandler = (
  state: RootState,
  assigneeId: number,
  assignmentHistory: IAssignmentHistory[]
) => {
  return ManApi.post('task/markHistory', assignmentHistory, {
    headers: { ...accessTokenHeader(state) },
    params: { assignmentId: assigneeId },
  });
};
